import { useContext, useEffect, useMemo, useState } from "react"
import Breadcrumb from "../../../../components/bootstrap/Breadcrumb"
import Card, { CardBody } from "../../../../components/bootstrap/Card"
import Nav, { NavItem } from "../../../../components/bootstrap/Nav"
import Page from "../../../../layout/Page/Page"
import PageWrapper from "../../../../layout/PageWrapper/PageWrapper"
import SelectEstablishment from "../../../../components/MyCustom/SelectEstablishment"
import SelectStore from "../../../../components/MyCustom/SelectStore"
import SubHeader, { SubHeaderLeft } from "../../../../layout/SubHeader/SubHeader"
import { demoPages } from "../../../../menu"

import IfoodManager from "./components/IfoodManager"
import RappiManager from "./components/RappiManager"
import Button from "../../../../components/bootstrap/Button"
import Spinner from "../../../../components/bootstrap/Spinner"
import { orderBy } from "firebase/firestore"
import CategoryDB from "../../../../database/wrappers/category"
import ProductDB from "../../../../database/wrappers/product"
import SubCategoryDB from "../../../../database/wrappers/subCategory"
import CategoryComplementDB from "../../../../database/wrappers/categoryComplement"
import ComplementDB from "../../../../database/wrappers/complement"
import StoreProductDB from "../../../../database/wrappers/storeProduct"
import StoreComplementDB from "../../../../database/wrappers/storeComplement"
import { DefaultContext } from "../../../../contexts/default"
import NineNineManager from "./components/NineNineManager"

const DELIVERY_TABS = {
    IFOOD: 'ifood',
    RAPPI: 'rappi',
    NINENINE: '99food'
}

const toDict = (items = []) => {
    const dictionary = {};
    items.forEach((item) => {
        dictionary[item.id] = item;
    });
    return dictionary;
};

const emptyBaseData = {
    categories: [],
    products: [],
    subCategories: [],
    complementCategories: [],
    complements: [],
    storeProducts: [],
    storeComplements: [],
};

const ManagementDeliveryV2 = () => {
    const { estabSelected, storeSelected } = useContext(DefaultContext);
    const [activeTab, setActiveTab] = useState(DELIVERY_TABS.IFOOD)
    const [triggerModal, setTriggerModal] = useState(null)
    const [deliveryBaseData, setDeliveryBaseData] = useState(emptyBaseData);
    const [loadingBaseData, setLoadingBaseData] = useState(false);
    const [baseDataError, setBaseDataError] = useState("");

    useEffect(() => {
        if (!estabSelected || !storeSelected) {
            setDeliveryBaseData(emptyBaseData);
            setBaseDataError("");
            setLoadingBaseData(false);
            return;
        }

        let mounted = true;
        setLoadingBaseData(true);
        setBaseDataError("");

        Promise.all([
            new CategoryDB(estabSelected).getAll(orderBy("name", "asc")),
            new ProductDB(estabSelected).getAll(),
            new SubCategoryDB(estabSelected).getAll(),
            new CategoryComplementDB(estabSelected).getAll(orderBy("name", "asc")),
            new ComplementDB(estabSelected).getAll(orderBy("name", "asc")),
            new StoreProductDB(estabSelected, storeSelected).getAll(),
            new StoreComplementDB(estabSelected, storeSelected).getAll(),
        ])
            .then(([
                categories,
                products,
                subCategories,
                complementCategories,
                complements,
                storeProducts,
                storeComplements,
            ]) => {
                if (!mounted) return;
                setDeliveryBaseData({
                    categories: categories || [],
                    products: products || [],
                    subCategories: subCategories || [],
                    complementCategories: complementCategories || [],
                    complements: complements || [],
                    storeProducts: storeProducts || [],
                    storeComplements: storeComplements || [],
                });
            })
            .catch(() => {
                if (!mounted) return;
                setBaseDataError("Falha ao carregar dados locais do estabelecimento/loja.");
                setDeliveryBaseData(emptyBaseData);
            })
            .finally(() => {
                if (mounted) setLoadingBaseData(false);
            });

        return () => {
            mounted = false;
        };
    }, [estabSelected, storeSelected]);

    const sharedLocalData = useMemo(() => {
        const categoriesById = toDict(deliveryBaseData.categories);
        const productsById = toDict(deliveryBaseData.products);
        const subCategoriesById = toDict(deliveryBaseData.subCategories);
        const complementCategoriesById = toDict(deliveryBaseData.complementCategories);
        const complementsById = toDict(deliveryBaseData.complements);
        const storeProductsById = toDict(deliveryBaseData.storeProducts);
        const storeComplementsById = toDict(deliveryBaseData.storeComplements);

        return {
            estabSelected,
            storeSelected,
            ...deliveryBaseData,
            categoriesById,
            productsById,
            subCategoriesById,
            complementCategoriesById,
            complementsById,
            storeProductsById,
            storeComplementsById,
        };
    }, [deliveryBaseData, estabSelected, storeSelected]);

    const handleOpenModal = () => {
        setTriggerModal(activeTab)
        setTimeout(() => setTriggerModal(null), 100)
    }

    const renderActiveTab = () => {
        switch (activeTab) {
            case DELIVERY_TABS.IFOOD:
                return (
                    <IfoodManager
                        shouldOpenModal={triggerModal === DELIVERY_TABS.IFOOD}
                        sharedLocalData={sharedLocalData}
                        loadingBaseData={loadingBaseData}
                    />
                )
            case DELIVERY_TABS.RAPPI:
                return (
                    <RappiManager
                        shouldOpenModal={triggerModal === DELIVERY_TABS.RAPPI}
                        sharedLocalData={sharedLocalData}
                        loadingBaseData={loadingBaseData}
                    />
                )
            case DELIVERY_TABS.NINENINE:
                return (
                    <NineNineManager
                        shouldOpenModal={triggerModal === DELIVERY_TABS.NINENINE}
                        sharedLocalData={sharedLocalData}
                        loadingBaseData={loadingBaseData}
                    />
                )
            default:
                return (
                    <IfoodManager
                        shouldOpenModal={triggerModal === DELIVERY_TABS.IFOOD}
                        sharedLocalData={sharedLocalData}
                        loadingBaseData={loadingBaseData}
                    />
                )
        }
    }

    return (
        <>
            <PageWrapper title={`${demoPages.delivery.subMenu.menuV2.text}`}>
                <SubHeader>
                    <SubHeaderLeft>
                        <Breadcrumb
                            list={[
                                { title: 'Delivery', to: '/delivery' },
                                {
                                    title: 'Menu V2',
                                    to: '/delivery/menuV2',
                                },
                            ]}
                        />
                    </SubHeaderLeft>
                </SubHeader>
                <Page container='fluid'>
                    <Card>
                        <CardBody className='p-5'>
                            <div className="row mb-4">
                                <SelectEstablishment />
                                <SelectStore />
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <Nav design='pills' className="gap-4">
                                    <NavItem isActive={activeTab === DELIVERY_TABS.IFOOD}>
                                        <Button
                                            type="button"
                                            onClick={() => setActiveTab(DELIVERY_TABS.IFOOD)}
                                            className={`btn btn-link text-decoration-none border border-primary`}
                                        >
                                            <span className="d-flex align-items-center">
                                                iFood
                                            </span>
                                        </Button>
                                    </NavItem>
                                    <NavItem isActive={activeTab === DELIVERY_TABS.RAPPI}>
                                        <Button
                                            type="button"
                                            onClick={() => setActiveTab(DELIVERY_TABS.RAPPI)}
                                            className={`btn btn-link text-decoration-none border border-primary`}
                                        >
                                            <span className="d-flex align-items-center">
                                                Rappi
                                            </span>
                                        </Button>
                                    </NavItem>
                                    <NavItem isActive={activeTab === DELIVERY_TABS.NINENINE}>
                                        <Button
                                            type="button"
                                            onClick={() => setActiveTab(DELIVERY_TABS.NINENINE)}
                                            className={`btn btn-link text-decoration-none border border-primary`}
                                        >
                                            <span className="d-flex align-items-center">
                                                99Food
                                            </span>
                                        </Button>
                                    </NavItem>
                                </Nav>

                                <Button
                                    color='dark'
                                    icon='Magic'
                                    shadow="sm"
                                    hoverShadow="sm"
                                    onClick={handleOpenModal}
                                >
                                    Cadastro de Menu
                                </Button>
                            </div>

                            <div className="tab-content">
                                {!estabSelected || !storeSelected ? (
                                    <div className="alert alert-info mb-0">
                                        Selecione estabelecimento e loja para carregar os dados de delivery.
                                    </div>
                                ) : loadingBaseData ? (
                                    <div className="d-flex justify-content-center p-5">
                                        <Spinner />
                                    </div>
                                ) : baseDataError ? (
                                    <div className="alert alert-danger mb-0">{baseDataError}</div>
                                ) : (
                                    renderActiveTab()
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Page>
            </PageWrapper >
        </>
    )
}

export default ManagementDeliveryV2
